import { StandardEvents } from '@shopify/events';

const root = document.querySelector('.nexita-asics-cro');
if (root && !root.dataset.asicsReady) {
  root.dataset.asicsReady = 'true';
  const product = JSON.parse(root.querySelector('[data-asics-cart-product]').textContent);
  const galleryCache = new Map();
  const initialGallery = root.querySelector('media-gallery');
  if (initialGallery) galleryCache.set(initialGallery.dataset.asicsColor || '', initialGallery.cloneNode(true));
  root.querySelectorAll('template[data-asics-gallery-color]').forEach(template => {
    const gallery = template.content.querySelector('media-gallery');
    if (gallery) galleryCache.set(template.dataset.asicsGalleryColor, gallery);
  });
  const localGalleryReady = initialGallery && [...root.querySelectorAll('[data-asics-color-options] input')].every(input => galleryCache.has(input.value));
  if (localGalleryReady) initialGallery.dataset.asicsLocalGallery = 'ready';

  // Warm the exact responsive image candidates; never delay the selection on a fetch.
  const warmedImages = new Map();
  const warmImage = image => {
    if (!image) return;
    if (warmedImages.has(image.src)) return warmedImages.get(image.src);
    const preload = new Image();
    warmedImages.set(image.src, preload);
    preload.decoding = 'async';
    if (image.sizes) preload.sizes = image.sizes;
    if (image.srcset) preload.srcset = image.srcset;
    preload.src = image.src;
    return preload;
  };
  const warmColor = color => {
    const gallery = galleryCache.get(color);
    gallery?.querySelectorAll('slideshow-slide img, .slideshow-controls__thumbnail img').forEach(warmImage);
  };
  const idle = window.requestIdleCallback || (callback => setTimeout(callback, 100));
  const warmPrimaryImages = () => idle(() => {
    if (navigator.connection?.saveData) return;
    galleryCache.forEach(gallery => warmImage(gallery.querySelector('slideshow-slide img')));
  });
  const initialImage = initialGallery?.querySelector('slideshow-slide img');
  if (!initialImage || initialImage.complete) warmPrimaryImages();
  else initialImage.addEventListener('load', warmPrimaryImages, { once: true });
  ['pointerover', 'focusin', 'pointerdown'].forEach(type => root.addEventListener(type, event => {
    const input = event.target.closest('.nexita-conversion-swatch')?.querySelector('input');
    if (input) warmColor(input.value);
  }, { passive: true }));

  function showColor(color) {
    if (!localGalleryReady) return;
    const gallery = root.querySelector('media-gallery');
    const cached = galleryCache.get(color);
    if (!gallery || !cached || gallery.dataset.asicsColor === color) return;
    const replacement = cached.cloneNode(true);
    replacement.dataset.asicsLocalGallery = 'ready';
    const hero = replacement.querySelector('slideshow-slide img');
    const preload = warmImage(cached.querySelector('slideshow-slide img'));
    const swatchInput = [...root.querySelectorAll('[data-asics-color-options] input')].find(input => input.value === color);
    const swatchImage = swatchInput?.closest('label')?.querySelector('img');
    // A cold high-resolution download must not leave the new colour blank.
    // Reuse its already-visible swatch until the full image is in the browser cache.
    if (hero && preload && !preload.complete && swatchImage?.complete && swatchImage.naturalWidth) {
      const src = hero.getAttribute('src');
      const srcset = hero.getAttribute('srcset');
      hero.removeAttribute('srcset');
      hero.src = swatchImage.currentSrc || swatchImage.src;
      preload.addEventListener('load', () => {
        if (srcset) hero.setAttribute('srcset', srcset);
        if (src) hero.setAttribute('src', src);
      }, { once: true });
    }
    // Keep Shopify's native slides, thumbnail indexes, zoom and keyboard controls.
    gallery.replaceWith(replacement);
    warmColor(color);
  }

  const initialVariant = new URL(location.href).searchParams.get('variant');
  // Shopping links have an intentional variant. Bare URLs require a size choice.
  const requiresSizeChoice = !!root.querySelector('[data-asics-size-options] input');
  let sizeConfirmed = !requiresSizeChoice || product.variants.some(variant => String(variant.id) === initialVariant);
  let selectionSequence = 0;
  const scrollToSizes = () => {
    const sizes = root.querySelector('[data-asics-size-options]');
    sizes?.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    const radio = sizes?.querySelector('input:checked:not(:disabled)') || sizes?.querySelector('input:not(:disabled)');
    radio?.focus({ preventScroll: true });
  };

  function syncSelection() {
    root.dataset.asicsSizeConfirmed = String(sizeConfirmed);
    const sizes = root.querySelector('[data-asics-size-options]');
    if (!sizeConfirmed) {
      sizes?.querySelectorAll('input').forEach(input => {
        input.checked = false;
        input.dataset.currentChecked = 'false';
      });
    }
    const add = root.querySelector('product-form-component add-to-cart-component');
    if (add && !add.parentElement.querySelector('[data-asics-choose-size]')) {
      const choose = document.createElement('button');
      choose.type = 'button';
      choose.className = 'button asics-cro-choose-size';
      choose.dataset.asicsChooseSize = '';
      choose.textContent = 'Choisir ma pointure';
      add.before(choose);
    }
    const bar = root.querySelector('[ref="stickyBar"]');
    if (bar && !bar.querySelector('[data-asics-choose-size]')) {
      const choose = document.createElement('button');
      choose.type = 'button';
      choose.className = 'button asics-cro-choose-size';
      choose.dataset.asicsChooseSize = '';
      choose.textContent = 'Pointure';
      bar.append(choose);
    }
    if (!sizeConfirmed) {
      const color = root.querySelector('[data-asics-color-options] input:checked')?.value || '';
      const text = root.querySelector('.sticky-add-to-cart__variant');
      if (text) text.textContent = [color, 'Pointure à choisir'].filter(Boolean).join(' · ');
    }
  }

  root.addEventListener('click', event => {
    const trigger = event.target.closest('[data-asics-choose-size], [data-asics-edit-selection]');
    if (trigger) {
      event.preventDefault();
      event.stopPropagation();
      scrollToSizes();
    }
  }, true);

  root.addEventListener('change', event => {
    if (event.target.matches('[data-asics-color-options] input')) showColor(event.target.value);
    if (event.target.matches('[data-asics-size-options] input:not(:disabled)')) sizeConfirmed = true;
  }, true);

  // Prevent Enter/submission during the unconfirmed or pending selection state.
  root.addEventListener('submit', event => {
    if (event.target.closest('product-form-component') && (!sizeConfirmed || root.dataset.asicsPending === 'true')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      scrollToSizes();
    }
  }, true);

  root.addEventListener(StandardEvents.productSelect, event => {
    const sequence = ++selectionSequence;
    root.dataset.asicsPending = 'true';
    event.promise?.then(() => {
      if (sequence !== selectionSequence) return;
      requestAnimationFrame(() => {
        root.dataset.asicsPending = 'false';
        syncSelection();
      });
    }).catch(() => {
      if (sequence === selectionSequence) root.dataset.asicsPending = 'false';
    });
  });
  syncSelection();
}
